import { apiRequest } from '@/utils/api-request';

export type QualificationStage = 'build' | 'pre_uat' | 'uat' | 'production';

export type ApplyOutcome =
  | 'admitted'
  | 'already_applied'
  | 'gate_failed'
  | 'no_open_wave';

export interface QualificationWave {
  id: string;
  client: string;
  name: string;
  region: string | null;
  pre_uat_opens_at: string;
  pre_uat_closes_at: string;
  uat_opens_at: string;
  uat_closes_at: string;
  status: 'planned' | 'open' | 'closed';
  requires_client_sign_off: boolean;
  required_checks: string[];
  advisory_checks: string[];
}

export interface GateEvaluation {
  key: string;
  passed: boolean;
  /** False means advisory: reported, but it does not block admission. */
  required: boolean;
  detail?: Record<string, unknown>;
}

export interface WaveEligibility {
  wave: QualificationWave;
  /** Reflects mandatory checks only. */
  can_apply: boolean;
  checks: GateEvaluation[];
}

export interface Eligibility {
  client: string;
  region: string;
  open_waves: WaveEligibility[];
  /**
   * Whether the control has anything to offer at all. A workflow already
   * in a wave returns `can_apply: true` on that wave alongside
   * `any_selectable: false`, so this is what a control gates on.
   */
  any_selectable: boolean;
  next_wave: QualificationWave | null;
  blocked_by_wave: QualificationWave | null;
  subject_id: string | null;
}

export type ApprovalSide = 'capitol' | 'client';
export type ApprovalDecision = 'approved' | 'rejected';

export interface OrgQualificationContext {
  client: string;
  region: string;
  /**
   * Every rung this org holds, not one. For an existing client the build
   * org and the production org are routinely the same main org.
   */
  stages: QualificationStage[];
}

export interface SideDecision {
  side: ApprovalSide;
  decision: ApprovalDecision | null;
  approver: string | null;
  feedback: string | null;
  decided_at: string | null;
}

export interface Incumbent {
  copy_workflow_id: string;
  org_id: string;
  entered_at: string;
  from_wave: string | null;
}

export interface Review {
  attempt_id: string;
  subject: { id: string; client_reference: string | null };
  wave: QualificationWave;
  status: string;
  current_stage: QualificationStage;
  awaiting_settlement: boolean;
  pre_uat_copy_workflow_id: string | null;
  incumbent: Incumbent | null;
  sides: SideDecision[];
  requires_client_sign_off: boolean;
  outstanding_sides: ApprovalSide[];
  /** Which side the viewer may sign as, or null if they may not sign. */
  viewer_side: ApprovalSide | null;
}

export interface WorkflowPlacement {
  stage: QualificationStage;
  org_id: string;
  copy_workflow_id: string;
  entered_at: string;
  review: Review;
}

export interface QualificationAttempt {
  id: string;
  wave_id: string;
  current_stage: QualificationStage;
  status: string;
  pinned_version_id: string | null;
  created_at: string;
}

export interface ApplyResult {
  outcome: ApplyOutcome;
  admitted: boolean;
  /** Whether the copy into the pre-UAT org happened. Applying again retries it. */
  placed: boolean;
  subject: { id: string };
  subject_created: boolean;
  wave: QualificationWave | null;
  attempt: QualificationAttempt | null;
  evaluations: GateEvaluation[];
  next_wave: QualificationWave | null;
}

/**
 * Thrown when the org appears in no qualification ladder. The ladder is
 * the enrolment record, so the endpoints 404 rather than 403 for an org
 * nobody has configured, and that is an expected answer for most orgs.
 */
export class NotEnrolledError extends Error {
  constructor() {
    super('Qualification is not enabled for this organization');
    this.name = 'NotEnrolledError';
  }
}

export async function getWorkflowEligibility(
  orgId: string,
  workflowId: string,
  signal?: AbortSignal,
): Promise<Eligibility> {
  const response = await apiRequest<Eligibility>({
    url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/qualification/eligibility`,
    method: 'GET',
    target: 'workflow',
    signal,
    suppressSentryStatuses: [404],
  });

  if (response.statusCode === 404) {
    throw new NotEnrolledError();
  }
  if (response.detail) {
    throw new Error(response.detail);
  }
  if (!response.data) {
    throw new Error('Empty response from the eligibility endpoint');
  }

  return response.data;
}

/**
 * A gate refusal returns 200 with `admitted: false` and the failing
 * checks. Only a transport or server fault throws.
 */
export async function applyToWave(
  orgId: string,
  workflowId: string,
  waveId: string,
  clientReference?: string,
): Promise<ApplyResult> {
  const response = await apiRequest<ApplyResult>({
    url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/qualification/apply`,
    method: 'POST',
    target: 'workflow',
    body: clientReference
      ? { wave_id: waveId, client_reference: clientReference }
      : { wave_id: waveId },
  });

  if (response.detail) {
    throw new Error(response.detail);
  }
  if (!response.data) {
    throw new Error('Empty response from the apply endpoint');
  }

  return response.data;
}

/**
 * Which rung(s) of which ladder an org occupies. One call per ORG, not
 * per workflow: it is what a list uses to decide which action to offer on
 * every row, so fetching it per row would issue one request per workflow.
 */
export async function getOrgQualificationContext(
  orgId: string,
  signal?: AbortSignal,
): Promise<OrgQualificationContext> {
  const response = await apiRequest<OrgQualificationContext>({
    url: `/api/v1/orgs/${orgId}/qualification/context`,
    method: 'GET',
    target: 'workflow',
    signal,
    suppressSentryStatuses: [404],
  });

  if (response.statusCode === 404) {
    throw new NotEnrolledError();
  }
  if (response.detail) {
    throw new Error(response.detail);
  }
  if (!response.data) {
    throw new Error('Empty response from the qualification context endpoint');
  }

  return response.data;
}

/** Thrown when a workflow is not a live qualification copy. */
export class NotAPlacementError extends Error {
  constructor() {
    super('This workflow is not a live qualification placement');
    this.name = 'NotAPlacementError';
  }
}

export async function getWorkflowPlacement(
  orgId: string,
  workflowId: string,
  signal?: AbortSignal,
): Promise<WorkflowPlacement> {
  const response = await apiRequest<WorkflowPlacement>({
    url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/qualification/placement`,
    method: 'GET',
    target: 'workflow',
    signal,
    suppressSentryStatuses: [404],
  });

  if (response.statusCode === 404) {
    throw new NotAPlacementError();
  }
  if (response.detail) {
    throw new Error(response.detail);
  }
  if (!response.data) {
    throw new Error('Empty response from the placement endpoint');
  }

  return response.data;
}

/**
 * Approve or reject at the pre-UAT gate. Refused unless the caller is on
 * the approver list for that client and region, so the control is only
 * offered when `viewer_side` is set.
 */
export async function submitDecision(
  attemptId: string,
  decision: ApprovalDecision,
  side: ApprovalSide,
  feedback?: string,
): Promise<Review> {
  const response = await apiRequest<Review>({
    url: `/api/v1/qualification/attempts/${attemptId}/decision`,
    method: 'POST',
    target: 'workflow',
    body: feedback ? { decision, side, feedback } : { decision, side },
  });

  if (response.detail) {
    throw new Error(response.detail);
  }
  if (!response.data) {
    throw new Error('Empty response from the decision endpoint');
  }

  return response.data;
}

export interface BoardPlacement {
  stage: QualificationStage;
  org_id: string;
  copy_workflow_id: string;
  entered_at: string;
}

export interface BoardRow {
  subject_id: string;
  client_reference: string | null;
  origin_workflow_id: string;
  origin_org_id: string;
  owner_user_id: string;
  workflow_name: string | null;
  attempt_id: string | null;
  wave_id: string | null;
  wave_name: string | null;
  current_stage: QualificationStage | null;
  status: string | null;
  outstanding_sides: ApprovalSide[];
  /** Signed off by everyone required and still in pre-UAT: waiting on the
   *  system, not on a person. */
  awaiting_settlement: boolean;
  placements: BoardPlacement[];
  updated_at: string;
}

export interface Board {
  client: string;
  region: string;
  /** Stated always, so an unreached region is never silently omitted. */
  regions_reached: number;
  regions_expected: number;
  rows: BoardRow[];
}

export async function getQualificationBoard(
  client: string,
  region: string,
  waveId?: string,
  signal?: AbortSignal,
): Promise<Board> {
  const params = new URLSearchParams({ client, region });
  if (waveId) params.set('wave_id', waveId);

  const response = await apiRequest<Board>({
    url: `/api/v1/qualification/board?${params.toString()}`,
    method: 'GET',
    target: 'workflow',
    signal,
  });

  if (response.detail) {
    throw new Error(response.detail);
  }
  if (!response.data) {
    throw new Error('Empty response from the board endpoint');
  }

  return response.data;
}

export interface PromotionResult {
  from_stage: QualificationStage;
  to_stage: QualificationStage;
  copy_workflow_id: string;
  placement: BoardPlacement;
  superseded: BoardPlacement | null;
}

/**
 * Move an attempt into the next stage. Into UAT this is the admin's
 * explicit action and the backend refuses it unless both sides have
 * signed, so the control is offered only where the gate is settled.
 */
export async function promoteAttempt(
  attemptId: string,
  toStage: QualificationStage,
  note?: string,
): Promise<PromotionResult> {
  const response = await apiRequest<PromotionResult>({
    url: `/api/v1/qualification/attempts/${attemptId}/promote`,
    method: 'POST',
    target: 'workflow',
    body: note ? { to_stage: toStage, note } : { to_stage: toStage },
  });

  if (response.detail) {
    throw new Error(response.detail);
  }
  if (!response.data) {
    throw new Error('Empty response from the promote endpoint');
  }

  return response.data;
}

/** Every wave for a client and region, for the board's filter. */
export async function listWaves(
  client: string,
  region: string,
  signal?: AbortSignal,
): Promise<QualificationWave[]> {
  const params = new URLSearchParams({ client, region });
  const response = await apiRequest<{ waves: QualificationWave[] }>({
    url: `/api/v1/qualification/waves?${params.toString()}`,
    method: 'GET',
    target: 'workflow',
    signal,
  });

  if (response.detail) {
    throw new Error(response.detail);
  }
  return response.data?.waves ?? [];
}

export interface GateCheck {
  key: string;
  description: string;
}

export interface WaveInput {
  client: string;
  name: string;
  region?: string | null;
  pre_uat_opens_at: string;
  pre_uat_closes_at: string;
  uat_opens_at: string;
  uat_closes_at: string;
  status?: 'planned' | 'open' | 'closed';
  requires_client_sign_off?: boolean;
  required_checks?: string[];
  advisory_checks?: string[];
}

/** Everything a wave amendment may change. Client and region are fixed at creation. */
export type WavePatch = Partial<Omit<WaveInput, 'client' | 'region'>>;

export async function listGateChecks(
  signal?: AbortSignal,
): Promise<GateCheck[]> {
  const response = await apiRequest<{ checks: GateCheck[] }>({
    url: '/api/v1/qualification/checks',
    method: 'GET',
    target: 'workflow',
    signal,
  });
  if (response.detail) throw new Error(response.detail);
  return response.data?.checks ?? [];
}

export async function createWave(body: WaveInput): Promise<QualificationWave> {
  const response = await apiRequest<QualificationWave>({
    url: '/api/v1/qualification/waves',
    method: 'POST',
    target: 'workflow',
    body: body as unknown as Record<string, unknown>,
  });
  if (response.detail) throw new Error(response.detail);
  if (!response.data) throw new Error('Empty response from the wave endpoint');
  return response.data;
}

export async function updateWave(
  waveId: string,
  patch: WavePatch,
): Promise<QualificationWave> {
  const response = await apiRequest<QualificationWave>({
    url: `/api/v1/qualification/waves/${waveId}`,
    method: 'PATCH',
    target: 'workflow',
    body: patch as unknown as Record<string, unknown>,
  });
  if (response.detail) throw new Error(response.detail);
  if (!response.data) throw new Error('Empty response from the wave endpoint');
  return response.data;
}

export interface ApproverList {
  emails: string[];
  /** Whether this is the wave's own list rather than the client default. */
  is_override: boolean;
}

export async function getApprovers(
  client: string,
  region: string,
  side: ApprovalSide,
  waveId?: string,
  signal?: AbortSignal,
): Promise<ApproverList> {
  const params = new URLSearchParams({ client, region, side });
  if (waveId) params.set('wave_id', waveId);
  const response = await apiRequest<ApproverList>({
    url: `/api/v1/qualification/approvers?${params.toString()}`,
    method: 'GET',
    target: 'workflow',
    signal,
  });
  if (response.detail) throw new Error(response.detail);
  return response.data ?? { emails: [], is_override: false };
}

/**
 * Replaces one side's list outright. The API is deliberately not additive:
 * "these are the client approvers" expects that to be the whole list.
 */
export async function setApprovers(
  client: string,
  region: string,
  side: ApprovalSide,
  emails: string[],
  waveId?: string,
  /** Wave scope only: drop this wave's own list and fall back to the default. */
  useDefault = false,
): Promise<ApproverList> {
  const response = await apiRequest<ApproverList>({
    url: '/api/v1/qualification/approvers',
    method: 'PUT',
    target: 'workflow',
    body: {
      client,
      region,
      side,
      emails,
      ...(waveId ? { wave_id: waveId, use_default: useDefault } : {}),
    },
  });
  if (response.detail) throw new Error(response.detail);
  return response.data ?? { emails: [], is_override: false };
}
